import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, TrendingUp, Heart, Calendar, Check, ArrowRight, Clock, Gift, Sparkles, Baby, MessageCircle } from 'lucide-react';
import ChatBot from '../components/chat/ChatBot';

const Landing = () => {
  // Countdown to end of month offer
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });
  const [showNudge, setShowNudge] = useState(false);
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      const difference = endOfMonth - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60)
        });
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000);
    return () => clearInterval(timer);
  }, []);

  // Show nudge after 30 seconds if user hasn't clicked
  useEffect(() => {
    const hasInteracted = sessionStorage.getItem('vida_mujer_cta_clicked');
    if (!hasInteracted) {
      const nudgeTimer = setTimeout(() => {
        setShowNudge(true);
        // Auto-hide after 10 seconds
        setTimeout(() => setShowNudge(false), 10000);
      }, 30000); // 30 seconds
      
      return () => clearTimeout(nudgeTimer);
    }
  }, []);

  const handleCTAClick = () => {
    sessionStorage.setItem('vida_mujer_cta_clicked', 'true');
    setShowNudge(false);
    document.querySelector('[data-testid="chat-trigger-btn"]')?.click();
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Retorno del 115%',
      description: 'Recupera más de lo que inviertes al cumplir tu plan de 20 años.'
    },
    {
      icon: Calendar,
      title: 'Ahorros cada 2 años',
      description: 'Recibe cheques de cumplimiento garantizados durante todo el plan.'
    },
    {
      icon: Heart,
      title: 'Protección Exclusiva',
      description: 'Cobertura de cáncer femenino y complicaciones del embarazo.'
    },
    {
      icon: Shield,
      title: 'Independencia Total',
      description: 'Tu póliza es personal, no depende de empleo ni pareja.'
    }
  ];

  const testimonials = [
    {
      name: 'María Fernanda G.',
      role: 'Directora de Marketing',
      quote: 'Pablo me ayudó a entender que proteger mi futuro era la mejor inversión. Ya recibí mi primer cheque de ahorro.',
      avatar: 'https://images.unsplash.com/photo-1600696444233-20accba67df3?w=100&h=100&fit=crop&crop=face'
    },
    {
      name: 'Carolina R.',
      role: 'Emprendedora',
      quote: 'Como empresaria, necesitaba algo que me diera tranquilidad sin depender de mi negocio. Vida Mujer fue la respuesta.',
      avatar: 'https://images.unsplash.com/photo-1712799891569-f651f9110365?w=100&h=100&fit=crop&crop=face'
    },
    {
      name: 'Andrea M.',
      role: 'Médica Especialista',
      quote: 'La cobertura de cáncer femenino fue lo que me convenció. Es un plan pensado realmente para nosotras.',
      avatar: 'https://images.pexels.com/photos/6564468/pexels-photo-6564468.jpeg?w=100&h=100&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Urgency Banner - Top */}
      <motion.div 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#0F2B5B] to-[#1a4080] text-white py-2 px-4"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-4 text-sm">
          <Gift size={18} className="text-[#E5D4CE]" />
          <span className="hidden sm:inline">
            <strong>Diagnóstico GRATIS</strong> por tiempo limitado
          </span>
          <span className="sm:hidden">
            <strong>Diagnóstico GRATIS</strong>
          </span>
          <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1">
            <Clock size={14} />
            <span className="font-mono font-bold">
              {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m
            </span>
          </div>
        </div>
      </motion.div>

      {/* Navigation */}
      <nav className="fixed top-10 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_womenlife-expert/artifacts/33njuh6q_Logo%20PL.png" 
              alt="Pablo López - Asesor Patrimonial" 
              className="h-12 w-12 rounded-full object-cover"
            />
            <div>
              <span className="font-semibold text-stone-900">Pablo López</span>
              <span className="block text-xs text-stone-500">Asesor Patrimonial</span>
            </div>
          </div>
          <div className="hidden md:block">
            <span className="text-xs text-stone-400">Soluciones respaldadas por SMNYL</span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <motion.div 
            className="lg:col-span-7 space-y-8"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <span className="inline-block px-4 py-2 rounded-full bg-green-100 text-green-700 text-sm font-medium mb-4">
                ✨ 100% Gratis • Sin compromiso • 2 minutos
              </span>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-playfair text-4xl md:text-5xl lg:text-6xl text-stone-900 leading-tight tracking-tight"
            >
              ¿Y si pudieras proteger a tu familia, ahorrar en vida y{' '}
              <span className="text-[#0F2B5B]">recuperar más de lo que inviertes?</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-stone-600 max-w-xl leading-relaxed"
            >
              Descubre en <strong>2 minutos</strong> cuánto podrías acumular en los próximos 20 años 
              mientras proteges a tu familia. <strong>Recuperas el 115% de tu inversión.</strong>
            </motion.p>

            {/* Value Props - Quick Scan */}
            <motion.div variants={fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-3 py-4">
              <div className="text-center p-3 rounded-xl bg-[#E5D4CE]/30">
                <p className="text-2xl font-bold text-[#0F2B5B]">115%</p>
                <p className="text-xs text-stone-600">Retorno garantizado</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-[#E5D4CE]/30">
                <p className="text-2xl font-bold text-[#0F2B5B]">$1.5M</p>
                <p className="text-xs text-stone-600">Cobertura cáncer</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-[#E5D4CE]/30">
                <p className="text-2xl font-bold text-[#0F2B5B]">Día 1</p>
                <p className="text-xs text-stone-600">Protección inmediata</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-[#E5D4CE]/30">
                <p className="text-2xl font-bold text-[#0F2B5B]">20 años</p>
                <p className="text-xs text-stone-600">Cheques cada 2 años</p>
              </div>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 relative">
              {/* Nudge tooltip */}
              <AnimatePresence>
                {showNudge && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.9 }}
                    className="absolute -top-16 left-0 bg-[#0F2B5B] text-white px-4 py-2 rounded-xl shadow-lg z-10"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">👋</span>
                      <span className="font-medium">¿Lista para tu diagnóstico?</span>
                    </div>
                    <div className="absolute bottom-0 left-8 transform translate-y-1/2 rotate-45 w-3 h-3 bg-[#0F2B5B]"></div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              <button 
                data-testid="hero-cta-btn"
                onClick={handleCTAClick}
                className="rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-105 hover:shadow-lg bg-[#0F2B5B] text-white flex items-center gap-2 animate-pulse"
              >
                <Sparkles size={20} />
                Hacer mi diagnóstico GRATIS
                <ArrowRight size={20} />
              </button>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex items-center gap-6 pt-4">
              <div className="flex -space-x-3">
                <img src="https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=40&h=40&fit=crop&crop=face" alt="" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=40&h=40&fit=crop&crop=face" alt="" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=40&h=40&fit=crop&crop=face" alt="" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                <img src="https://images.unsplash.com/photo-1598550874175-4d0ef436c909?w=40&h=40&fit=crop&crop=face" alt="" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
              </div>
              <p className="text-sm text-stone-500">
                <span className="font-semibold text-stone-900">+500 mujeres en Jalisco</span> ya protegen su futuro con Pablo
              </p>
            </motion.div>
          </motion.div>

          <motion.div 
            className="lg:col-span-5"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#E5D4CE]/40 to-transparent rounded-3xl transform rotate-3" />
              <img
                src="https://customer-assets.emergentagent.com/job_financial-advisor-12/artifacts/bg34b9bo_hyper-realistic_portrait_of_a_confident_and_elegant_latin_woman_35-40_years_old_successful_professi_0knhjqkmv0yzx7nalc39_1.jpg"
                alt="Mujer profesional latina - Asesora patrimonial"
                className="relative rounded-3xl shadow-2xl w-full object-cover"
                style={{ maxHeight: '600px' }}
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl">
                <p className="text-xl font-playfair text-[#0F2B5B] font-semibold">Triple Beneficio</p>
                <p className="text-sm text-stone-500">Salud, ahorro y familia</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-playfair text-3xl md:text-4xl text-stone-900 mb-4">
              Beneficios diseñados para ti
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Vida Mujer combina protección, ahorro e inversión en un solo producto, 
              con coberturas exclusivas para mujeres.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                data-testid={`benefit-card-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="bg-stone-50 border border-stone-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#E5D4CE]/50 flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-[#0F2B5B]" />
                </div>
                <h3 className="font-semibold text-lg text-stone-900 mb-2">{benefit.title}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-br from-[#E5D4CE]/20 to-transparent">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-playfair text-3xl md:text-4xl text-stone-900 mb-4">
              ¿Cómo funciona?
            </h2>
            <p className="text-stone-600">Un proceso simple para asegurar tu futuro</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Conversa con el chat automatizado', desc: 'Responde algunas preguntas y recibe tu diagnóstico personalizado.' },
              { step: '02', title: 'Agenda tu asesoría con Pablo', desc: 'Una sesión breve para resolver dudas técnicas y legales.' },
              { step: '03', title: 'Contrata tu plan ideal', desc: 'Comienza a construir tu patrimonio desde el primer día.' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <span className="text-8xl font-playfair text-[#0F2B5B]/10 absolute -top-8 left-0">
                  {item.step}
                </span>
                <div className="pt-12">
                  <h3 className="font-semibold text-xl text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-playfair text-3xl md:text-4xl text-stone-900 mb-4">
              Mujeres que ya protegen su futuro
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                data-testid={`testimonial-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-stone-50 border border-stone-100 rounded-2xl p-8"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map(i => (
                    <span key={i} className="text-[#8DA399]">★</span>
                  ))}
                </div>
                <p className="text-stone-700 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-medium text-stone-900">{testimonial.name}</p>
                    <p className="text-sm text-stone-500">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-[#0F2B5B] rounded-3xl p-12 md:p-16 text-center text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
            <div className="relative z-10">
              <h2 className="font-playfair text-3xl md:text-4xl mb-4">
                ¿Lista para dar el siguiente paso?
              </h2>
              <p className="text-white/70 mb-8 max-w-xl mx-auto">
                Conversa con mi asistente ahora y descubre cuál es el plan ideal para ti.
                Sin compromiso, sin presión.
              </p>
              <button
                data-testid="cta-chat-btn"
                onClick={() => document.querySelector('[data-testid="chat-trigger-btn"]')?.click()}
                className="rounded-full px-8 py-4 text-lg font-medium bg-white text-[#0F2B5B] hover:bg-[#E5D4CE] transition-all hover:scale-105"
              >
                Quiero mi diagnóstico gratis ahora
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WhatsApp Direct CTA - Floating LEFT side */}
      <a
        href="https://wa.me/523329186014?text=Hola%20Pablo,%20me%20interesa%20Vida%20Mujer"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-40 bg-[#25D366] text-white px-4 py-3 rounded-full shadow-lg hover:scale-105 transition-all flex items-center gap-2 group"
      >
        <MessageCircle size={20} />
        <span className="hidden group-hover:inline text-sm font-medium">Escríbele a Pablo</span>
      </a>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 border-t border-stone-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_womenlife-expert/artifacts/33njuh6q_Logo%20PL.png" 
                alt="Pablo López - Asesor Patrimonial" 
                className="h-12 w-12 rounded-full object-cover"
              />
              <div>
                <span className="font-semibold text-stone-900">Pablo López</span>
                <span className="block text-xs text-stone-500">Asesor Patrimonial Certificado</span>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-stone-100 text-center">
            <p className="text-xs text-stone-400 max-w-2xl mx-auto">
              Pablo López es Asesor Profesional de Seguros independiente. No es empleado de Seguros Monterrey New York Life. 
              Los productos mencionados están respaldados por Seguros Monterrey New York Life.
            </p>
            <p className="text-xs text-stone-400 mt-4">
              © {new Date().getFullYear()} Pablo López - Todos los derechos reservados
            </p>
          </div>
        </div>
      </footer>

      {/* ChatBot */}
      <ChatBot />
    </div>
  );
};

export default Landing;
